import dayjs from 'dayjs';
import type { BrandForLogo } from '/@/components/bayer/hero-header/hero-brand-logo';
import {
    DEFAULT_ENVIRONMENT_VARIABILITY,
    type DensitySimulationSnapshot,
    toSimulationSnapshot,
} from '/@/views/recommendations/v2/steps/management/density-simulation-data';
import {
    DEFAULT_PLANTER_EFFICIENCY,
    deriveBagFigures,
    FALLBACK_HYBRID_COLOR,
    HYBRID_COLORS,
    type ManagementHybrid,
} from '/@/views/recommendations/v2/steps/management/use-management-data';
import { CRITICAL_PERIOD_DAYS, type HybridPhenology } from '/@/views/recommendations/v2/steps/management/use-phenology';

/**
 * The saved recommendation's product entry, as the PDF service receives it — the backup blob
 * camelized on its way out of Django.
 */
type SavedCurve = { density: number; estimatedYield: number }[];

type SavedProduct = {
    id: number;
    catalogName?: string;
    /** The product's own brand, which is how a recommendation can span more than one. */
    brand?: BrandForLogo;
    seedsPerBag?: number;
    gdus?: { emergence?: number; flowering?: number; maturity?: number };
    /** `[planting, emergence, flowering, maturity]`. */
    phenology?: string[];
    /** Already plants/ha, unlike `recommended.density`. */
    harvestDensity?: { recommended?: number };
    recommended?: { yield?: number; density?: number };
    estimatedYield?: { forRecommendedHarvestDensity?: number };
    /** Curve points, densities already in plants/ha. */
    estimatedYieldByHarvestDensity?: SavedCurve;
    /**
     * The same curve for the density simulator's better and worse environments. Only saved when
     * the recommendation has a habitual density (`userDensity`).
     */
    simulationCurves?: { over?: SavedCurve; under?: SavedCurve };
    fertilization?: {
        n?: number | { common?: number; recommended?: number };
        p?: number;
        k?: number;
        s?: number;
    };
    /**
     * Recommended density per environment range, for products with a data-backed table.
     * Nested under `profile` in the saved blob (`useRecommendationResultData`'s "Deprecated"
     * `output.profile` block) rather than top-level, unlike the live catalog product shape.
     */
    profile?: {
        densityTableValues?: { value: number; minEnvironment?: number; maxEnvironment?: number; order?: number }[];
        /** Where the legacy template keeps the brand the v2 payload puts at the top level. */
        product?: { brand?: BrandForLogo };
    };
};

export type SavedCropConfig = {
    crop?: string;
    products?: number[];
    productRecommendations?: SavedProduct[];
    plantingDate?: string;
    environment?: number;
    climatePattern?: number;
    soilMoisture?: number;
    userPlanterEfficiency?: number;
    /** The user's area override, in hectares — `user_area` on the serialized crop. */
    userArea?: number | null;
    /** The habitual density from the simulator, plants/ha; 0 or null when not given. */
    userDensity?: number | null;
    /** The simulator's better/worse environment shifts, in %. */
    userBetterEnvironment?: number;
    userWorseEnvironment?: number;
};

type SavedNitrogen = number | { common?: number; recommended?: number } | undefined;

/** Mirrors `nitrogenFrom`: the hybrid-specific figure wins, and callers need to know which. */
const readNitrogen = (n: SavedNitrogen) => {
    if (n === undefined || n === null) return { value: undefined, isRecommended: false };
    if (typeof n === 'number') return { value: n, isRecommended: false };
    if (typeof n.recommended === 'number') return { value: n.recommended, isRecommended: true };
    return { value: n.common, isRecommended: false };
};

export const getPlanterEfficiency = (cropConfig?: SavedCropConfig) =>
    cropConfig?.userPlanterEfficiency || DEFAULT_PLANTER_EFFICIENCY;

/**
 * The saved products, in the shape the density cards, curve and nutrient table already read.
 *
 * Everything comes from the stored blob — the PDF renders server-side and can't fetch, which
 * is the same constraint the v1 template works under. Note the two density scales in that
 * blob: `recommended.density` is on the API's own scale (7.7) while `harvestDensity` and the
 * curve points are already plants/ha, so no anchor multiplier belongs here.
 */
export const toManagementHybrids = ({
    cropConfig,
    areaInHectares,
}: {
    cropConfig?: SavedCropConfig;
    areaInHectares?: number;
}): ManagementHybrid[] => {
    const products = cropConfig?.productRecommendations || [];
    const planterEfficiency = getPlanterEfficiency(cropConfig);
    // Selection order drives the colours, so the PDF's dots match the web page's.
    const order = cropConfig?.products || products.map((product) => product.id);

    return products
        .slice()
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
        .map((product, index) => {
            const harvestDensity = product.harvestDensity?.recommended;
            const bags = deriveBagFigures({
                harvestDensity,
                planterEfficiency,
                seedsPerBag: product.seedsPerBag,
                areaInHectares,
            });
            const nitrogen = readNitrogen(product.fertilization?.n);

            return {
                id: product.id,
                catalogName: product.catalogName || '',
                color: HYBRID_COLORS[index % HYBRID_COLORS.length] ?? FALLBACK_HYBRID_COLOR,
                harvestDensity,
                estimatedYield: product.estimatedYield?.forRecommendedHarvestDensity ?? product.recommended?.yield,
                nitrogen: nitrogen.value,
                nitrogenIsRecommended: nitrogen.isRecommended,
                phosphorus: product.fertilization?.p,
                potassium: product.fertilization?.k,
                sulfur: product.fertilization?.s,
                ...bags,
                points: (product.estimatedYieldByHarvestDensity || []).map((point) => ({
                    density: point.density,
                    yield: point.estimatedYield,
                })),
                densityTableValues: product.profile?.densityTableValues || [],
            };
        });
};

/**
 * The saved stage dates, in the timeline's shape.
 *
 * The blob stores phenology as `[planting, emergence, flowering, maturity]` and has no slot for
 * V6, so the PDF omits that marker — the timeline only draws it when present.
 */
export const toPhenologyHybrids = (cropConfig?: SavedCropConfig): HybridPhenology[] => {
    const products = cropConfig?.productRecommendations || [];
    const order = cropConfig?.products || products.map((product) => product.id);

    return products
        .slice()
        .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
        .map((product, index) => {
            const [planting, emergence, flowering, maturity] = product.phenology || [];
            const gdus = product.gdus || {};
            const segments = [gdus.emergence, gdus.flowering, gdus.maturity].filter(
                (value): value is number => typeof value === 'number'
            );

            return {
                id: product.id,
                catalogName: product.catalogName || '',
                color: HYBRID_COLORS[index % HYBRID_COLORS.length] ?? FALLBACK_HYBRID_COLOR,
                planting,
                emergence,
                flowering,
                maturity,
                gdus,
                totalGdus: segments.length ? segments.reduce((total, value) => total + value, 0) : undefined,
                criticalPeriod: flowering
                    ? {
                          start: dayjs(flowering).subtract(CRITICAL_PERIOD_DAYS, 'day').toISOString(),
                          end: dayjs(flowering).add(CRITICAL_PERIOD_DAYS, 'day').toISOString(),
                      }
                    : undefined,
            };
        });
};

/**
 * The brands the hero shows a logo for.
 *
 * Taken from the selected products rather than the recommendation's single `brand`, because one
 * recommendation can mix brands — a Dekalb hybrid beside a Preceon one — and each of them has a
 * mark of its own to carry. Deduped by id in selection order, so the hero reads left-to-right
 * like the tables below it. Falls back to the recommendation's brand for payloads saved before
 * the products carried one.
 *
 * `profile.product.brand` is the legacy template's location for the same object — the v1 hero
 * already resolves its single brand from there — so both payload shapes are read.
 */
export const toHeroBrands = (
    cropConfig?: { productRecommendations?: Pick<SavedProduct, 'brand' | 'profile'>[] },
    fallback?: BrandForLogo
): BrandForLogo[] => {
    const seen = new Set<number | string>();
    const brands = (cropConfig?.productRecommendations || [])
        .map((product) => product.brand || product.profile?.product?.brand)
        .filter((brand): brand is BrandForLogo => !!brand)
        .filter((brand) => {
            const key = brand.id ?? brand.name ?? '';
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });

    return brands.length ? brands : [fallback].filter((brand): brand is BrandForLogo => !!brand);
};

const toCurvePoints = (curve?: SavedCurve) =>
    curve?.map((point) => ({ density: point.density, yield: point.estimatedYield }));

/**
 * The density/environment simulation, one per hybrid, from the saved curves — each hybrid with
 * the snapshot its section is drawn from.
 *
 * Empty when the recommendation has no habitual density — the section compares against it, so
 * without one there is nothing to show. A hybrid with no curve (a table-backed product) is left
 * out, the same as the wizard's simulator leaves it out.
 */
export const toDensitySimulations = ({
    cropConfig,
    hybrids,
}: {
    cropConfig?: SavedCropConfig;
    hybrids: ManagementHybrid[];
}): { hybrid: ManagementHybrid; snapshot: DensitySimulationSnapshot }[] => {
    if (!cropConfig?.userDensity) return [];
    const betterPercent = cropConfig.userBetterEnvironment ?? DEFAULT_ENVIRONMENT_VARIABILITY;
    const worsePercent = cropConfig.userWorseEnvironment ?? betterPercent;

    return hybrids
        .filter((hybrid) => hybrid.points.length > 0 && hybrid.harvestDensity)
        .map((hybrid) => {
            const product = cropConfig.productRecommendations?.find(({ id }) => id === hybrid.id);
            const snapshot = toSimulationSnapshot({
                hybridName: hybrid.catalogName,
                environment: cropConfig.environment,
                betterPercent,
                worsePercent,
                recommendedDensity: hybrid.harvestDensity,
                recommendedYield: hybrid.estimatedYield,
                habitualDensity: cropConfig.userDensity ?? undefined,
                curves: {
                    over: toCurvePoints(product?.simulationCurves?.over),
                    estimated: hybrid.points,
                    under: toCurvePoints(product?.simulationCurves?.under),
                },
            });
            return snapshot ? { hybrid, snapshot } : undefined;
        })
        .filter((entry): entry is { hybrid: ManagementHybrid; snapshot: DensitySimulationSnapshot } => !!entry);
};
